import type { ObjectiveTerm } from '@moment/horizon-data-access/proposal'
import type { FilterObject } from '@moment/trading-types/filter-expression'
import type { QueryClient } from '@tanstack/react-query'
import type { LiveInventoryReturnValues } from '@/types/api/live-inventory'
import type { SecuritySchemaInfo } from './fetch-securities'
import type { SecurityReferenceDataAccess } from './security-access-where'
import type {
  QuoteYieldField,
  SecurityInventoryItem,
} from './security-row-adapter'
import type { SecurityTaxInputs } from './security-tax-expressions'
import type { QuoteSideKey, SecurityRow, SecurityWhere } from './security-types'
import type { ObjectivePlan } from './translate-objective'
import type { SecuritySortKey } from './translate-sort'

import { createLogger } from '@/lib/logger'

import {
  fetchFilterListMembership,
  fetchSecuritiesWithBooks,
  fetchSecurityPage,
  loadSecuritySchema,
  securityFieldSpecs,
  securityRequestParams,
} from './fetch-securities'
import { securityAccessWhere } from './security-access-where'
import {
  hasTopOfBook,
  instrumentFieldPairs,
  toSecurityInventoryItem,
} from './security-row-adapter'
import { quotedFlagField, sideQuoteFieldNames } from './security-wire-fields'
import {
  qualifySecurityField,
  translateFilterObject,
} from './translate-filter-object'
import { translateObjective } from './translate-objective'
import { sortTaxColumns, translateSort } from './translate-sort'

const logger = createLogger('shared/sorm/security/fetch-live-inventory-items')

interface SecurityReadInput {
  returns: ReadonlyArray<LiveInventoryReturnValues | QuoteYieldField>
  /** A risk group already validated against the caller's (see `validRiskGroupId`). */
  riskGroupId: string
  /**
   * Narrows the read to the org's reference-data entitlements. Omit it for
   * bonds the client already holds or orders, which must render whatever their
   * asset type, currency or ISIN country.
   */
  access?: SecurityReferenceDataAccess
  useEvalsAsOrders: boolean
  /** The investor's tax inputs, inlined into tax-aware yields and objectives. */
  tax?: SecurityTaxInputs
  /**
   * The org's filter-list IDs (`useGetFilterListsMap`), in list order. A
   * requested `filter_lists` holds the ones that list the row's ISIN, CUSIP or
   * ticker.
   */
  filterListIds?: ReadonlyArray<string>
  queryName: string
  signal?: AbortSignal
}

export interface LiveInventoryItemGroup {
  key: string
  isins: ReadonlyArray<string>
  /** Book side the group is scored and filtered against. */
  side: QuoteSideKey
  objectiveTerms?: ReadonlyArray<ObjectiveTerm>
  filter?: FilterObject
}

export interface FetchLiveInventoryItemsInput extends SecurityReadInput {
  groups: ReadonlyArray<LiveInventoryItemGroup>
  /**
   * Read every ISIN's full order book from `Quote` in the same snapshot, so
   * `asks`/`bids` carry depth past the best level. Evals-as-orders orgs keep
   * their single MPE level.
   */
  bookDepth?: boolean
}

// Undefined means the group has no objective; a translation failure throws.
function planObjective(
  terms: ReadonlyArray<ObjectiveTerm> | undefined,
  side: QuoteSideKey,
  useEvalsAsOrders: boolean,
  isFieldAvailable: (field: string) => boolean,
  tax: SecurityTaxInputs | undefined,
): ObjectivePlan | undefined {
  if (!terms || terms.length === 0) return undefined
  const plan = translateObjective(terms, {
    side,
    useEvalsAsOrders,
    isFieldAvailable,
    tax,
  })
  if (plan.unresolvedFields.length > 0) {
    logger.warn('Objective fields Security does not serve score as zero', {
      fields: plan.unresolvedFields,
    })
  }
  return plan
}

// Level 0 stays Security's best quote (post-fee, with pre-fee and markup);
// deeper levels come from the Quote book. No best quote means no market.
function withBookDepth(
  best: SecurityInventoryItem['asks'],
  book: SecurityInventoryItem['asks'] | undefined,
): SecurityInventoryItem['asks'] {
  if (best.length === 0) return best
  return [...best, ...(book ?? []).slice(1)]
}

interface SecurityRead {
  schema: SecuritySchemaInfo
  isFieldAvailable: (field: string) => boolean
  requestParams: Record<string, unknown>
  baseFields: string[]
  where: (side: QuoteSideKey, filter?: FilterObject) => SecurityWhere[]
  adapt: (
    rows: ReadonlyArray<SecurityRow>,
    objective?: ObjectivePlan,
  ) => SecurityInventoryItem[]
}

async function prepareSecurityRead(
  queryClient: QueryClient,
  input: SecurityReadInput,
): Promise<SecurityRead> {
  const schema = await loadSecuritySchema(queryClient)
  const fieldPairs = instrumentFieldPairs(input.returns, input.useEvalsAsOrders)
  const accessWhere = input.access ? securityAccessWhere(input.access) : []
  return {
    schema,
    isFieldAvailable: (field) => schema.fields.has(field),
    requestParams: securityRequestParams(input.riskGroupId),
    baseFields: [
      ...fieldPairs.map(([, securityField]) => securityField),
      ...sideQuoteFieldNames('ask'),
      ...sideQuoteFieldNames('bid'),
    ],
    where: (side, filter) => [
      ...accessWhere,
      ...(filter ? [translateFilterObject(filter, { side })] : []),
    ],
    adapt: (rows, objective) =>
      rows.map((row) =>
        toSecurityInventoryItem(row, {
          fieldPairs,
          decimalFields: schema.decimalFields,
          useEvalsAsOrders: input.useEvalsAsOrders,
          objective,
          scoresObjective: input.returns.includes('objective_score'),
        }),
      ),
  }
}

function warnIfUnquoted(
  items: ReadonlyArray<SecurityInventoryItem>,
  riskGroupId: string,
): void {
  if (items.length > 0 && !items.some(hasTopOfBook)) {
    logger.warn('Security response carried no quotes for its risk group', {
      riskGroupId,
      rows: items.length,
    })
  }
}

function hasFilter(filter: FilterObject | undefined): boolean {
  return filter !== undefined && Object.keys(filter).length > 0
}

// Without a row filter, every requested ISIN should come back; one that does
// not is missing from Security's reference data.
function warnMissingIsins(
  groups: ReadonlyArray<LiveInventoryItemGroup>,
  itemsByKey: Map<string, SecurityInventoryItem[]>,
): void {
  for (const group of groups) {
    if (hasFilter(group.filter)) continue
    const returned = new Set(
      (itemsByKey.get(group.key) ?? []).map((item) => String(item.isin)),
    )
    const missing = group.isins.filter((isin) => !returned.has(isin))
    if (missing.length > 0) {
      logger.warn('Security returned no row for requested ISINs', {
        group: group.key,
        missing,
      })
    }
  }
}

/**
 * Live-inventory-shaped items read from SORM `Security`, one list per group.
 * Every group goes out in one batch-query so all rows share one order-book
 * snapshot. A filter or objective Security cannot express rejects with
 * `UnsupportedSecurityTranslationError` rather than widening or going unscored.
 */
export async function fetchSecurityInventoryItems(
  queryClient: QueryClient,
  input: FetchLiveInventoryItemsInput,
): Promise<Map<string, SecurityInventoryItem[]>> {
  const read = await prepareSecurityRead(queryClient, input)
  const withFilterLists = input.returns.includes('filter_lists')
  const isins = input.groups.flatMap((group) => group.isins)

  const plans = new Map<string, ObjectivePlan | undefined>()
  const groups = input.groups.map((group) => {
    const objective = planObjective(
      group.objectiveTerms,
      group.side,
      input.useEvalsAsOrders,
      read.isFieldAvailable,
      input.tax,
    )
    plans.set(group.key, objective)
    return {
      key: group.key,
      isins: group.isins,
      fields: securityFieldSpecs(
        read.baseFields,
        read.schema,
        objective?.fields,
        input.tax,
      ),
      where: read.where(group.side, group.filter),
      requestParams: read.requestParams,
    }
  })

  const withDepth = Boolean(input.bookDepth) && !input.useEvalsAsOrders
  const { rowsByKey, books, filterListsByIsin } =
    await fetchSecuritiesWithBooks(
      groups,
      {
        books: withDepth
          ? { isins, riskGroupId: input.riskGroupId }
          : undefined,
        filterLists: withFilterLists
          ? { isins, listIds: input.filterListIds ?? [] }
          : undefined,
      },
      { queryName: input.queryName, signal: input.signal },
    )

  const itemsByKey = new Map<string, SecurityInventoryItem[]>()
  for (const [key, rows] of rowsByKey) {
    const items = read.adapt(rows, plans.get(key))
    itemsByKey.set(
      key,
      items.map((item) => {
        if (withFilterLists) {
          item.filter_lists = filterListsByIsin.get(String(item.isin)) ?? []
        }
        if (!withDepth) return item
        const book = books.get(String(item.isin))
        return {
          ...item,
          asks: withBookDepth(item.asks, book?.asks),
          bids: withBookDepth(item.bids, book?.bids),
        }
      }),
    )
  }
  warnMissingIsins(input.groups, itemsByKey)
  warnIfUnquoted([...itemsByKey.values()].flat(), input.riskGroupId)
  return itemsByKey
}

export interface FetchSecurityInventoryPageInput extends SecurityReadInput {
  /** Universe reads narrow to the org's reference-data entitlements. */
  access: SecurityReferenceDataAccess
  /** Book side the page is scored, filtered and sorted against. */
  side: QuoteSideKey
  objectiveTerms?: ReadonlyArray<ObjectiveTerm>
  filter?: FilterObject
  /** Keep only rows quoted on `side`, as live inventory's `side` argument does. */
  quotedOnly: boolean
  sort: ReadonlyArray<SecuritySortKey>
  offset: number
  limit: number
}

export interface SecurityInventoryPage {
  items: SecurityInventoryItem[]
  totalCount: number
}

/**
 * One filtered, sorted page of live-inventory-shaped items from SORM
 * `Security`. Sort keys Security cannot serve are dropped with a warning. A
 * requested `filter_lists` is read for the page's ISINs in a second batch.
 */
export async function fetchSecurityInventoryPage(
  queryClient: QueryClient,
  input: FetchSecurityInventoryPageInput,
): Promise<SecurityInventoryPage> {
  const read = await prepareSecurityRead(queryClient, input)
  const objective = planObjective(
    input.objectiveTerms,
    input.side,
    input.useEvalsAsOrders,
    read.isFieldAvailable,
    input.tax,
  )
  const fields = securityFieldSpecs(
    [...read.baseFields, ...sortTaxColumns(input.sort, input.useEvalsAsOrders)],
    read.schema,
    objective?.fields,
    input.tax,
  )
  const { sort, unsupportedFields, unmappedFields } = translateSort(
    input.sort,
    {
      side: input.side,
      useEvalsAsOrders: input.useEvalsAsOrders,
      hasObjective: objective !== undefined,
      isFieldAvailable: read.isFieldAvailable,
      isTaxColumnProjected: (column) =>
        fields.some((field) => field.alias === column),
    },
  )
  if (unmappedFields.length > 0) {
    logger.warn('Sort keys with no Security mapping were dropped', {
      fields: unmappedFields,
    })
  }
  if (unsupportedFields.length > 0) {
    logger.warn('Sort keys Security does not serve were dropped', {
      fields: unsupportedFields,
    })
  }
  const where = read.where(input.side, input.filter)
  if (input.quotedOnly) {
    where.push({
      [qualifySecurityField(quotedFlagField(input.side))]: { equal_to: true },
    })
  }

  const page = await fetchSecurityPage(
    {
      fields,
      where,
      requestParams: read.requestParams,
      sort,
      offset: input.offset,
      limit: input.limit,
    },
    { queryName: input.queryName, signal: input.signal },
  )
  const items = read.adapt(page.rows, objective)
  warnIfUnquoted(items, input.riskGroupId)
  if (input.returns.includes('filter_lists')) {
    const filterListsByIsin = await fetchFilterListMembership(
      {
        isins: items.map((item) => String(item.isin)),
        listIds: input.filterListIds ?? [],
      },
      { queryName: input.queryName, signal: input.signal },
    )
    for (const item of items) {
      item.filter_lists = filterListsByIsin.get(String(item.isin)) ?? []
    }
  }
  return { items, totalCount: page.totalCount }
}
